import React from "react";
import { 
  Calendar, 
  Clock, 
  User, 
  ArrowRight, 
  Share2, 
  Tag, 
  Facebook, 
  Twitter, 
  Linkedin,
  Dumbbell
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardFooter, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Separator } from "@/components/ui/separator";
import { Link } from "@/components/common/Link";
import { PostsProvider, usePosts } from "@/integrations/wordpress/WordPressPostsProvider";
import { WP_Query } from "@/integrations/wordpress/wp_query";

declare const wvcClient: any;



function RelatedPostsConsumer() {
  const { posts, loading, error } = usePosts();

  if (loading) {
    return (
      <div data-section-id="14115" className="grid grid-cols-1 gap-6 md:grid-cols-3">
        {[...Array(3)].map((_, i) => (
          <div key={i} data-index={i} className="h-96 animate-pulse rounded-[3px] bg-muted/20" />
        ))}
      </div>
    );
  }

  if (error || posts.length === 0) {
    return null; 
  }

  return (
    <div className="grid grid-cols-1 gap-8 md:grid-cols-3">
      {posts.map((post, i) => (
        <Card key={post.id} data-index={i} className="group flex h-full flex-col overflow-hidden border-border/50 bg-card transition-all hover:border-primary/50 hover:shadow-sm rounded-[3px]">
          <div className="relative aspect-video w-full overflow-hidden bg-muted">
            {post.featuredImage ? (
              <img 
                src={post.featuredImage} 
                alt={post.title} 
                className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
              />
            ) : (
              <div className="flex h-full w-full items-center justify-center bg-secondary text-primary/40">
                <Dumbbell className="h-12 w-12 opacity-20" />
              </div>
            )}
            <div className="absolute top-3 left-3 rounded-[3px] bg-primary px-2 py-1 text-xs font-bold text-primary-foreground uppercase tracking-wider font-sans">
              Insikt
            </div>
          </div>
          <CardHeader className="pb-2">
            <div className="mb-2 flex items-center gap-2 text-xs text-primary/70 font-sans">
              <Calendar className="h-3 w-3" />
              <span>{post.date ? new Date(post.date).toLocaleDateString('sv-SE') : "Senaste"}</span>
            </div>
            <CardTitle className="line-clamp-2 text-xl group-hover:text-primary font-serif font-bold text-primary">
              <Link to={post.link || "#"}>
                {post.title}
              </Link>
            </CardTitle>
          </CardHeader>
          <CardContent className="flex-grow pb-4">
            <div 
              className="line-clamp-3 text-sm text-primary/80 font-sans"
              dangerouslySetInnerHTML={{ __html: post.excerpt || "" }} 
            />
          </CardContent>
          <CardFooter className="pt-0">
            <Link to={post.link || "#"} className="w-full">
              <Button variant="outline" className="w-full group-hover:border-primary group-hover:text-primary rounded-[3px] font-sans border-primary/20 text-primary">
                Läs artikeln <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
            </Link>
          </CardFooter>
        </Card>
      ))}
    </div>
  );
}

function RelatedPostsWrapper({ currentPostId }: { currentPostId: string }) {
  
  const relatedQuery = new WP_Query({
    posts_per_page: 3,
    post_type: "post",
    post__not_in: [parseInt(currentPostId)],
    ignore_sticky_posts: true,
  });

  return (
    <div className="mt-24">
      <div className="mb-10 flex items-center justify-between">
        <h3 className="text-3xl font-bold tracking-tight md:text-4xl font-serif text-primary">Relaterade artiklar</h3>
        <Link to="/InsightsPage">
          <Button variant="ghost" className="hidden sm:flex font-sans text-primary hover:text-primary/80">
            Se alla artiklar <ArrowRight className="ml-2 h-4 w-4" />
          </Button>
        </Link>
      </div>
      <PostsProvider wp_query={relatedQuery}>
        <RelatedPostsConsumer />
      </PostsProvider>
      <div className="mt-8 flex justify-center sm:hidden">
        <Link to="/InsightsPage">
          <Button variant="outline" className="rounded-[3px] font-sans text-primary border-primary">
            Se alla artiklar <ArrowRight className="ml-2 h-4 w-4" />
          </Button>
        </Link>
      </div>
    </div>
  );
}



function SinglePostConsumer() {
  const { posts, loading, error, refetch } = usePosts();

  if (loading) {
    return (
      <div className="container mx-auto max-w-4xl px-4 py-20">
        <div className="mb-8 h-12 w-3/4 animate-pulse rounded-[3px] bg-muted/20" />
        <div className="mb-12 aspect-video w-full animate-pulse rounded-[3px] bg-muted/20" />
        <div className="space-y-4">
          <div className="h-4 w-full animate-pulse rounded bg-muted/20" />
          <div className="h-4 w-full animate-pulse rounded bg-muted/20" />
          <div className="h-4 w-5/6 animate-pulse rounded bg-muted/20" />
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="container mx-auto flex min-h-[50vh] flex-col items-center justify-center px-4 text-center">
        <h2 className="mb-4 text-2xl font-bold font-serif text-primary">Kunde inte ladda artikeln</h2>
        <p className="mb-6 text-primary/70 font-sans">Något gick fel när innehållet skulle hämtas.</p>
        <Button onClick={refetch} className="rounded-[3px] bg-primary text-primary-foreground">Försök igen</Button>
      </div>
    );
  }

  if (!posts || posts.length === 0) {
    return (
      <div className="container mx-auto flex min-h-[50vh] flex-col items-center justify-center px-4 text-center">
        <h2 className="mb-4 text-2xl font-bold font-serif text-primary">Artikeln hittades inte</h2>
        <Link to="/HomePage">
          <Button className="rounded-[3px] bg-primary text-primary-foreground">Tillbaka till start</Button>
        </Link>
      </div>
    );
  }

  const post = posts[0];

  return (
    <div className="container mx-auto px-4 py-12 md:py-20">
      <article className="mx-auto max-w-4xl">
        {}
        <header className="mb-10 text-center md:mb-14">
          <div className="mb-6 flex flex-wrap items-center justify-center gap-4 text-sm font-medium text-primary/70 font-sans">
            {post.categories && post.categories.length > 0 && (
              <span className="rounded-[3px] bg-primary/10 px-3 py-1 text-primary">
                {post.categories[0].name}
              </span>
            )}
            <div className="flex items-center gap-1.5">
              <Calendar className="h-4 w-4" />
              <span>{post.date ? new Date(post.date).toLocaleDateString('sv-SE', { year: 'numeric', month: 'long', day: 'numeric' }) : ""}</span>
            </div>
            <div className="flex items-center gap-1.5">
              <Clock className="h-4 w-4" />
              <span>5 min läsning</span>
            </div>
          </div>
          
          <h1 className="mb-8 text-4xl font-bold leading-tight tracking-tight md:text-5xl lg:text-6xl font-serif text-primary">
            {post.title}
          </h1>

          {}
          {post.author && (
            <div className="flex items-center justify-center gap-3">
              <Avatar className="h-12 w-12 border-2 border-background shadow-sm">
                <AvatarImage src={post.author.avatar?.['96'] || ""} alt={post.author.name} />
                <AvatarFallback className="bg-primary/10 text-primary"><User className="h-5 w-5" /></AvatarFallback>
              </Avatar>
              <div className="text-left font-sans">
                <p className="text-sm font-semibold text-primary">Av {post.author.name}</p>
                <p className="text-xs text-primary/60">Expert inom autonom körning</p>
              </div>
            </div>
          )}
        </header>

        {}
        {post.featuredImage && (
          <div className="mb-12 overflow-hidden rounded-[3px] shadow-sm ring-1 ring-border/50">
            <img 
              src={post.featuredImage} 
              alt={post.title} 
              className="aspect-video w-full object-cover"
            />
          </div>
        )}

        {}
        <div className="prose prose-lg prose-neutral dark:prose-invert max-w-none font-sans
          [&_p]:mb-6 [&_p]:leading-relaxed [&_p]:text-primary
          [&_h2]:mt-12 [&_h2]:mb-6 [&_h2]:text-3xl [&_h2]:font-bold [&_h2]:tracking-tight [&_h2]:text-primary [&_h2]:font-serif
          [&_h3]:mt-10 [&_h3]:mb-4 [&_h3]:text-2xl [&_h3]:font-semibold [&_h3]:text-primary [&_h3]:font-serif
          [&_ul]:mb-8 [&_ul]:list-disc [&_ul]:pl-6 [&_ul]:text-primary
          [&_ol]:mb-8 [&_ol]:list-decimal [&_ol]:pl-6 [&_ol]:text-primary
          [&_li]:mb-2
          [&_blockquote]:border-l-4 [&_blockquote]:border-primary [&_blockquote]:pl-6 [&_blockquote]:italic [&_blockquote]:text-primary
          [&_img]:rounded-[3px] [&_img]:shadow-sm
          [&_a]:text-primary [&_a]:underline [&_a]:underline-offset-4 hover:[&_a]:text-primary/80
        ">
          <div dangerouslySetInnerHTML={{ __html: post.content || "" }} />
        </div>

        {}
        <div className="mt-16 flex flex-col gap-6 border-y border-border py-8 md:flex-row md:items-center md:justify-between">
          <div className="flex flex-wrap items-center gap-2 font-sans">
            <Tag className="h-4 w-4 text-primary/60" />
            <span className="mr-2 text-sm font-medium text-primary/60">Taggar:</span>
            {post.tags && post.tags.length > 0 ? (
              post.tags.map((tag, i) => (
                <span key={i} className="rounded-[3px] bg-secondary px-2.5 py-1 text-xs font-medium text-primary hover:bg-secondary/80">
                  #{tag.name}
                </span>
              ))
            ) : (
              <span className="text-sm text-primary/60">Robotaxi, Tesla, Framtid</span>
            )}
          </div>
          
          <div className="flex items-center gap-4">
            <span className="text-sm font-medium text-primary/60 font-sans">Dela:</span>
            <div className="flex gap-2">
              <Button size="icon" variant="outline" className="h-9 w-9 rounded-full hover:border-primary hover:text-primary border-primary/20 text-primary">
                <Facebook className="h-4 w-4" />
              </Button>
              <Button size="icon" variant="outline" className="h-9 w-9 rounded-full hover:border-primary hover:text-primary border-primary/20 text-primary">
                <Twitter className="h-4 w-4" />
              </Button>
              <Button size="icon" variant="outline" className="h-9 w-9 rounded-full hover:border-primary hover:text-primary border-primary/20 text-primary">
                <Linkedin className="h-4 w-4" />
              </Button>
              <Button size="icon" variant="outline" className="h-9 w-9 rounded-full hover:border-primary hover:text-primary border-primary/20 text-primary">
                <Share2 className="h-4 w-4" />
              </Button>
            </div>
          </div>
        </div>

        {}
        {post.author && (
          <div className="mt-12 rounded-[3px] bg-secondary/30 p-8 md:p-10">
            <div className="flex flex-col items-center gap-6 text-center md:flex-row md:text-left">
              <Avatar className="h-24 w-24 border-4 border-background shadow-sm">
                <AvatarImage src={post.author.avatar?.['96'] || ""} alt={post.author.name} />
                <AvatarFallback className="text-2xl bg-primary/10 text-primary"><User /></AvatarFallback>
              </Avatar>
              <div className="flex-1 font-sans">
                <h3 className="mb-2 text-xl font-bold text-primary font-serif">Om {post.author.name}</h3>
                <p className="mb-4 text-primary/80">
                  Expert på framtidens transportlösningar och robotaxins intåg på den svenska marknaden. Passionerad för hur teknologi kan förändra våra städer.
                </p>
                <Link to={post.author.link || "#"}>
                  <Button variant="link" className="h-auto p-0 text-primary font-semibold hover:text-primary/80">
                    Visa profil
                  </Button>
                </Link>
              </div>
            </div>
          </div>
        )}
      </article>

      {}
      <RelatedPostsWrapper currentPostId={post.id} />
    </div>
  );
}

export default function SinglePostTemplateSection() {
  
  
  let wp_query;
  try {
    const current_wp_query_params = wvcClient.getCurrentWPQueryParams();
    wp_query = new WP_Query(current_wp_query_params);
  } catch (e) {
    
    wp_query = new WP_Query({ post_type: "post", p: 1 });
  }

  return (
    <section id="single-post-template-section" className="bg-background text-primary">
      <PostsProvider wp_query={wp_query}>
        <SinglePostConsumer />
      </PostsProvider>
    </section>
  );
}